import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";

import { DisputeNotice } from "@/components/billing/DisputeNotice";
import { ErrorState, Spinner, Badge } from "@/components/ui";
import { portal } from "@/lib/api";
import { formatDateTime, formatMoney } from "@/lib/format";
import type { PaymentAttempt, PaymentAttemptState, PaymentAttemptTransition } from "@/lib/types";

type Tone = "success" | "danger" | "warning" | "neutral";

/** What each state means to the customer (docs/payment-attempts.md has the support wording). */
export const STATES: Record<PaymentAttemptState, { label: string; tone: Tone; hint: string }> = {
  processing: { label: "Processing", tone: "warning", hint: "Waiting for the payment processor." },
  successful: { label: "Successful", tone: "success", hint: "The payment was taken." },
  failed: { label: "Failed", tone: "danger", hint: "Nothing was taken." },
  needs_customer_action: {
    label: "Needs approval",
    tone: "warning",
    hint: "Your bank asked you to approve this payment. If it isn't approved within 24 hours it is cancelled and nothing is charged.",
  },
  unknown: {
    label: "Not confirmed",
    tone: "warning",
    hint: "We could not confirm whether it went through. Please don't pay again yet.",
  },
  refunded: { label: "Refunded", tone: "neutral", hint: "The payment was paid back in full." },
  refund_failed: {
    label: "Refund failed",
    tone: "danger",
    hint: "The refund was refused. The original payment is still taken.",
  },
};

const SOURCES: Record<PaymentAttemptTransition["source"], string> = {
  provider_response: "Payment processor's answer",
  webhook: "Processor notification",
  reconciliation: "Checked with the processor",
  refund: "Refund",
  system: "Portal",
};

/** Every try to collect one invoice, oldest first, each with its history and refunds. */
export function PaymentAttempts({ orgId, invoiceId }: { orgId: string; invoiceId: string }) {
  const attempts = useQuery({
    queryKey: ["payment-attempts", orgId, invoiceId],
    queryFn: () => portal.paymentAttempts(orgId, invoiceId),
  });
  if (attempts.isPending) return <Spinner />;
  if (attempts.isError) return <ErrorState error={attempts.error} />;
  const list = attempts.data.payment_attempts;
  if (list.length === 0) {
    return (
      <p className="text-sm text-fg-subtle">
        No payment attempts were recorded for this invoice. Payments made before attempts were tracked have
        none.
      </p>
    );
  }
  return (
    <ol className="flex flex-col gap-4">
      {list.map((a, n) => (
        <AttemptCard key={a.id} attempt={a} title={`Attempt ${n + 1}`} />
      ))}
    </ol>
  );
}

/** One attempt: its state, amount and card, why it failed, its history and its refunds. */
export function AttemptCard({ attempt: a, title }: { attempt: PaymentAttempt; title: ReactNode }) {
  const state = STATES[a.state];
  const label = typeof title === "string" ? title.toLowerCase() : "attempt";
  const money = (cents: number) => formatMoney(cents, a.currency);
  return (
    <li className="rounded-xl border border-border bg-surface p-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <p className="text-sm font-semibold text-fg">{title}</p>
        <Badge tone={state.tone}>{state.label}</Badge>
        <p className="text-sm text-fg-muted">
          {money(a.amount_cents)}
          {a.payment_method_label && ` · ${a.payment_method_label}`}
        </p>
        <p className="ml-auto text-xs text-fg-subtle">{formatDateTime(a.created_at)}</p>
      </div>
      <p className="mt-1 text-xs text-fg-subtle">{state.hint}</p>
      {a.failure_reason && a.state !== "successful" && (
        <p className="mt-2 text-sm text-[#b8291f]">{a.failure_reason}</p>
      )}

      <ol className="mt-3 space-y-1 border-l border-border pl-4" aria-label={`History of ${label}`}>
        {a.transitions.map((t) => (
          <li key={t.sequence} className="text-xs text-fg-muted">
            <span className="font-semibold text-fg">{STATES[t.to_state].label}</span>
            {" · "}
            {formatDateTime(t.occurred_at)}
            {" · "}
            {SOURCES[t.source]}
          </li>
        ))}
      </ol>

      {a.disputes.length > 0 && (
        <ul className="mt-3 space-y-1" aria-label={`Bank queries about ${label}`}>
          {a.disputes.map((d) => (
            <DisputeNotice key={d.id} dispute={d} attempt={a} />
          ))}
        </ul>
      )}

      {a.refunds.length > 0 && (
        <ul className="mt-3 space-y-1" aria-label={`Refunds of ${label}`}>
          {a.refunds.map((r) => (
            <li key={r.id} className="text-xs text-fg-muted">
              <span className="font-semibold text-fg">
                {r.status === "succeeded" ? "Refunded" : "Refund refused"} {money(r.amount_cents)}
              </span>
              {" · "}
              {formatDateTime(r.created_at)}
              {r.failure_reason && <span className="text-[#b8291f]"> · {r.failure_reason}</span>}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
